import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Headphones, Flag, MessageSquare, RefreshCw, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

export function CustomerSupportDashboard() {
  const { hasRole } = useAuth();
  const isLead = hasRole('customer_support_lead');

  const { data, isLoading } = useQuery({
    queryKey: ['cs-stats'],
    queryFn: async () => {
      const since = new Date(Date.now() - 30 * 86400_000).toISOString();
      const [{ count: openReports }, { count: openDisputes }, { count: msgs30d }] = await Promise.all([
        supabase.from('reports' as any).select('*', { count: 'exact', head: true }).eq('status', 'open'),
        supabase.from('lead_charges' as any).select('*', { count: 'exact', head: true }).eq('status', 'disputed'),
        supabase.from('messages' as any).select('*', { count: 'exact', head: true }).gte('created_at', since),
      ]);
      return { openReports: openReports || 0, openDisputes: openDisputes || 0, msgs30d: msgs30d || 0 };
    },
  });

  if (isLoading) return <Skeleton className="h-96" />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Headphones className="h-6 w-6 text-primary" />
          Customer Support {isLead ? 'Lead' : 'Agent'}
        </h1>
        <p className="text-sm text-muted-foreground">Handle complaints, refunds, dispute escalations, and platform support tickets.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card><CardContent className="p-4 flex items-center gap-3">
          <Flag className="h-8 w-8 text-red-500" />
          <div><p className="text-2xl font-bold">{data?.openReports}</p><p className="text-xs text-muted-foreground">Open Reports</p></div>
        </CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3">
          <AlertCircle className="h-8 w-8 text-amber-500" />
          <div><p className="text-2xl font-bold">{data?.openDisputes}</p><p className="text-xs text-muted-foreground">Lead Disputes (open)</p></div>
        </CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3">
          <MessageSquare className="h-8 w-8 text-blue-500" />
          <div><p className="text-2xl font-bold">{data?.msgs30d}</p><p className="text-xs text-muted-foreground">Messages (30d)</p></div>
        </CardContent></Card>
      </div>

      <Card>
        <CardHeader><CardTitle>Quick Actions</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Button asChild variant="outline" className="justify-start"><Link to="/dashboard/reports"><Flag className="h-4 w-4 mr-2" /> Reports & Abuse Queue</Link></Button>
          <Button asChild variant="outline" className="justify-start"><Link to="/dashboard/lead-marketplace"><RefreshCw className="h-4 w-4 mr-2" /> Lead Disputes</Link></Button>
          <Button asChild variant="outline" className="justify-start"><Link to="/dashboard/messages"><MessageSquare className="h-4 w-4 mr-2" /> Messages</Link></Button>
        </CardContent>
      </Card>
    </div>
  );
}
